import { Grid, Skeleton, capitalize, styled } from '@mui/material';
import React from 'react';
import HrCard from 'routes/HumanResourcesModule/components/HrCard';
import { RetrospectiveT } from '../../helpers/types';
import {
    EnveloppesData,
    useGetEnveloppesData,
} from '../helpers/retrospectiveData';
import { currencyFormatter, formatDisplay } from 'helpers/formatHelper';
import { StyPaper } from 'routes/HumanResourcesModule/Simulation/SimulationSynthesis/GVT/PayConstants/PayConstants';
import Flex from 'ui/Flex/Flex';
import {
    StyBlackBoldText,
    StyPrimaryBoldText,
    StyPrimaryText,
    StyText,
} from '../helpers/StyTypography';
import { AbsentAndSeasonalEmployeesCost } from './AbsentAndSeasonalEmployeesCost';
import { useTranslation } from 'react-i18next';

const StyHrCard = styled(HrCard)(() => ({
    '.MuiCardHeader-action': { mr: 0 },
    height: '100%',
}));

const PayComponentsCost = ({
    retrospective,
    data,
    isLoading,
}: {
    retrospective: RetrospectiveT;
    data: EnveloppesData;
    isLoading: boolean;
}): React.ReactElement => {
    const { t } = useTranslation();
    const formattedDate = capitalize(
        new Date(retrospective.createdAt).toLocaleString('fr-FR', {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
        }),
    );
    return (
        <>
            {isLoading ? (
                <Skeleton variant="rectangular" width="100%" height="100%" />
            ) : (
                <StyPaper
                    sx={{
                        height: '100%',
                        padding: '1rem',
                        marginBottom: '0.2rem',
                    }}
                >
                    <Grid container gap={2}>
                        <Grid item xs={12}>
                            <Flex justifyContent="space-between">
                                <StyBlackBoldText>
                                    {t(
                                        'humanResources.retrospective.envelopes.payComponents',
                                    )}
                                </StyBlackBoldText>

                                <StyPrimaryBoldText>
                                    {currencyFormatter(
                                        data.totalHc +
                                            data.totalHs +
                                            data.totalAstreintes,
                                    )}
                                </StyPrimaryBoldText>
                            </Flex>
                        </Grid>
                        <Grid item xs={12}>
                            <Flex justifyContent="space-between">
                                <StyText>
                                    {t(
                                        'humanResources.retrospective.envelopes.supplementaryHours',
                                    )}
                                </StyText>
                                <Flex justifyContent="flex-end" gap={3}>
                                    <StyPrimaryText>
                                        {formatDisplay(
                                            data.hoursHs,
                                            'integer',
                                            0,
                                        )}
                                        h
                                    </StyPrimaryText>
                                    <StyPrimaryText>
                                        {currencyFormatter(data.totalHs)}
                                    </StyPrimaryText>
                                </Flex>
                            </Flex>
                        </Grid>

                        <Grid item xs={12}>
                            <Flex justifyContent="space-between">
                                <StyText>
                                    {t(
                                        'humanResources.retrospective.envelopes.compensationHours',
                                        {
                                            startDate: formattedDate,
                                            endDate: formattedDate,
                                        },
                                    )}
                                </StyText>
                                <Flex justifyContent="flex-end" gap={3}>
                                    <StyPrimaryText>
                                        {formatDisplay(
                                            data.hoursHc,
                                            'integer',
                                            0,
                                        )}
                                        h
                                    </StyPrimaryText>
                                    <StyPrimaryText>
                                        {currencyFormatter(data.totalHc)}
                                    </StyPrimaryText>
                                </Flex>
                            </Flex>
                        </Grid>
                        <Grid item xs={12}>
                            <Flex justifyContent="space-between">
                                <StyText>
                                    {t(
                                        'humanResources.retrospective.envelopes.astreintes',
                                        {
                                            startDate: formattedDate,
                                            endDate: formattedDate,
                                        },
                                    )}
                                </StyText>
                                <StyPrimaryText>
                                    {currencyFormatter(data.totalAstreintes)}
                                </StyPrimaryText>
                            </Flex>
                        </Grid>
                    </Grid>
                </StyPaper>
            )}
        </>
    );
};

export const EnveloppesAmounts = ({
    retrospective,
}: {
    retrospective: RetrospectiveT;
}): React.ReactElement => {
    const { t } = useTranslation();
    const {
        data: enveloppesDataResponse,
        isLoading: isEnveloppesDataResponseLoading,
    } = useGetEnveloppesData(retrospective?.id ?? 0);

    // The amounts are initialized with 0 and then updated with the requested data. The missing values are filled with 0
    let totalAmountsData: EnveloppesData = {
        hoursHs: 0,
        hoursHc: 0,
        hoursAstreintes: 0,
        totalAstreintes: 0,
        totalHs: 0,
        totalHc: 0,
        absenceCost: 0,
        absenceEtp: 0,
        seasonalCost: 0,
        seasonalEtp: 0,
    };
    const enveloppesData = enveloppesDataResponse ?? {};
    totalAmountsData = Object.keys(totalAmountsData).reduce((acc, key) => {
        acc[key as keyof EnveloppesData] =
            (enveloppesData as EnveloppesData)[key as keyof EnveloppesData] ??
            0;
        return acc;
    }, {} as EnveloppesData);
    return (
        <>
            {isEnveloppesDataResponseLoading ? (
                <Skeleton
                    variant="rectangular"
                    sx={{ width: '100%', height: '29rem', borderRadius: '4px' }}
                />
            ) : (
                <StyHrCard
                    title={t(
                        'humanResources.retrospective.envelopes.variablesEnveloppes',
                    )}
                >
                    <Grid container minHeight="25rem" height="100%" spacing={2}>
                        <Grid item xs={6} minHeight="25rem">
                            <PayComponentsCost
                                retrospective={retrospective}
                                data={totalAmountsData}
                                isLoading={false}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <AbsentAndSeasonalEmployeesCost
                                data={totalAmountsData}
                                isLoading={false}
                            />
                        </Grid>
                    </Grid>
                </StyHrCard>
            )}
        </>
    );
};
